import { notifyUnauthorized } from './api';
import type { Draw } from '../types/api';

/**
 * The close code the server uses when it has ended this session deliberately.
 *
 * In the private range, and distinct from every transport failure, so the
 * client can tell "your session is over" from "the network blinked" — the two
 * want opposite responses.
 */
const SESSION_ENDED_CLOSE_CODE = 4001;

const WS_URL = import.meta.env.VITE_WS_URL ?? 'ws://localhost:4000/ws';

/**
 * How often the console asks the server to re-answer everything it is watching.
 *
 * The server already pushes on every change, so this is the safety net rather
 * than the mechanism: it closes the window where a change was published while
 * the socket was reconnecting, and it costs one small frame a screen.
 */
const SYNC_INTERVAL_MS = 10_000;

export interface LiveState {
  connected: boolean;
  drawNo: number | null;
  periodCode: string | null;
  secondsRemaining: number | null;
  lastResults: Draw[] | null;
  lastEventAt: number | null;
}

type Listener = (event: string, data: Record<string, unknown>) => void;

/** A table's standing request: "keep me looking at this". */
interface Subscription {
  id: string;
  resource: string;
  params: Record<string, unknown>;
  onData: (payload: unknown) => void;
  onError: (error: { status: number; code: string; message: string }) => void;
}

export interface LiveError {
  status: number;
  code: string;
  message: string;
}

/**
 * Single shared socket for the whole console.
 *
 * The admin channel carries draw lifecycle and registration events; every screen
 * subscribes to the same connection rather than opening its own. Reconnects with
 * a capped backoff, because an operator leaves this open all day and a laptop
 * that sleeps should recover on its own.
 */
class RealtimeClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private token: string | null = null;
  private closedByUs = false;
  private subscriptions = new Map<string, Subscription>();
  private syncTimer: number | null = null;
  private nextId = 0;

  connect(token: string | null) {
    this.token = token;
    this.closedByUs = false;
    this.open();
  }

  private open() {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const url = this.token ? `${WS_URL}?token=${encodeURIComponent(this.token)}` : WS_URL;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      // Everything the screens were watching is re-established before anything
      // else, so a reconnect is invisible rather than a set of frozen tables.
      this.subscriptions.forEach((subscription) => this.sendSubscribe(subscription));
      this.startSync();
      this.emit('__open', {});
    };

    socket.onmessage = (message) => {
      try {
        const parsed = JSON.parse(message.data as string) as { event: string; data: Record<string, unknown> };
        // Live answers go to the one screen that asked; everything else is an
        // announcement and goes to whoever is listening.
        if (parsed.event === 'live.data' || parsed.event === 'live.error') {
          this.routeLiveFrame(parsed.event, parsed.data ?? {});
          return;
        }
        /*
         * The server is about to close this socket because the session behind
         * it has ended — most often because the account signed in somewhere
         * else, since one account holds one session.
         *
         * Acted on here rather than left to the next failed request: the frame
         * arrives at the moment it happens and names the cause, where waiting
         * would leave the console looking signed in until something made it
         * ask, and then telling the operator only that "something" was refused.
         */
        if (parsed.event === 'session.ended') {
          notifyUnauthorized(String(parsed.data?.code ?? 'SESSION_REVOKED'));
          return;
        }
        this.emit(parsed.event, parsed.data ?? {});
      } catch {
        /* a frame we cannot parse is not worth tearing the socket down for */
      }
    };

    socket.onclose = (event) => {
      this.stopSync();
      this.emit('__close', {});
      // 4001 is the server saying this session is over, not that the link
      // dropped. Reconnecting would open a socket on a token it has already
      // refused, on a backoff, for as long as the tab stays open.
      if (event.code === SESSION_ENDED_CLOSE_CODE) return;
      if (!this.closedByUs) this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 15_000);
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private emit(event: string, data: Record<string, unknown>) {
    this.listeners.forEach((listener) => listener(event, data));
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /* ── Live queries ─────────────────────────────────────────────────────── */

  /**
   * Watches a server-side resource until the returned function is called.
   *
   * The data arrives three ways and the caller cannot tell them apart, which is
   * the point: once on subscribing, again whenever a write changes it, and again
   * on the heartbeat. A screen renders whatever it was last handed.
   */
  watch(
    resource: string,
    params: Record<string, unknown>,
    onData: (payload: unknown) => void,
    onError: (error: LiveError) => void,
  ): () => void {
    const id = `s${++this.nextId}`;
    const subscription: Subscription = { id, resource, params, onData, onError };
    this.subscriptions.set(id, subscription);
    this.sendSubscribe(subscription);

    return () => {
      this.subscriptions.delete(id);
      this.send({ type: 'unsubscribe', id });
    };
  }

  private routeLiveFrame(event: string, data: Record<string, unknown>) {
    const subscription = this.subscriptions.get(String(data.id));
    if (!subscription) return;
    if (event === 'live.data') {
      subscription.onData(data.payload);
      return;
    }

    const failure = {
      status: Number(data.status ?? 0),
      code: String(data.code ?? 'LIVE_FAILED'),
      message: String(data.message ?? 'Could not refresh'),
    };
    // A refused token here means the same as a 401 on any other request: the
    // session has ended, and the console should say so rather than sit on
    // tables that have quietly stopped updating.
    if (failure.status === 401) notifyUnauthorized(failure.code);
    subscription.onError(failure);
  }

  private sendSubscribe({ id, resource, params }: Subscription) {
    this.send({ type: 'subscribe', id, resource, params });
  }

  private send(message: unknown) {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private startSync() {
    this.stopSync();
    this.syncTimer = window.setInterval(() => {
      if (this.subscriptions.size) this.send({ type: 'sync' });
    }, SYNC_INTERVAL_MS);
  }

  private stopSync() {
    if (this.syncTimer === null) return;
    clearInterval(this.syncTimer);
    this.syncTimer = null;
  }

  /** Re-reads everything now — after a write, rather than waiting for the beat. */
  syncNow() {
    if (this.subscriptions.size) this.send({ type: 'sync' });
  }

  disconnect() {
    this.closedByUs = true;
    this.stopSync();
    this.subscriptions.clear();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }
}

export const realtime = new RealtimeClient();
